import React, {useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import TemplateDefault from "../../components/template-default";
import demandOrganizationService from "../../services/demand-organization-service";
import BoardFormModal from "./components/board-form-modal";
import BoardsLibrary from "./components/boards-library";

const MySwal = withReactContent(Swal);

const DemandOrganization = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [formOpen, setFormOpen] = useState(false);
    const [editingBoard, setEditingBoard] = useState(null);

    const {data: boardsData, isLoading: boardsLoading} = useQuery({
        queryKey: ["demand-organization"],
        queryFn: () => demandOrganizationService.list(),
        retry: 0,
    });

    const boards = useMemo(() => Array.isArray(boardsData) ? boardsData : boardsData?.data ?? [], [boardsData]);

    const {data: usersData, isLoading: usersLoading} = useQuery({
        queryKey: ["demand-organization-users"],
        queryFn: () => demandOrganizationService.getUserOptions(),
        retry: 0,
    });

    const userOptions = useMemo(() => {
        const responseUsers = Array.isArray(usersData) ? usersData : usersData?.data ?? [];
        return responseUsers.map(user => ({value: user.value, label: user.label}));
    }, [usersData]);

    const refreshBoards = () => queryClient.invalidateQueries({queryKey: ["demand-organization"]});
    const saveMutationOptions = {
        onSuccess: async () => {
            await refreshBoards();
            toast.success("Salvo com sucesso.");
        },
        onError: () => toast.error("Não foi possível salvar o quadro."),
    };
    const createBoard = useMutation({mutationFn: values => demandOrganizationService.create(values), ...saveMutationOptions});
    const updateBoard = useMutation({mutationFn: ({id, values}) => demandOrganizationService.edit(values, id), ...saveMutationOptions});
    const copyBoard = useMutation({
        mutationFn: id => demandOrganizationService.copy(id),
        onSuccess: refreshBoards,
        onError: () => toast.error("Não foi possível copiar o quadro."),
    });
    const deleteBoard = useMutation({
        mutationFn: id => demandOrganizationService.delete(id),
        onSuccess: async () => {
            await refreshBoards();
            toast.success("Quadro excluído com sucesso.");
        },
        onError: () => toast.error("Não foi possível excluir o quadro."),
    });

    const closeForm = () => {
        setFormOpen(false);
        setEditingBoard(null);
    };

    const saveBoard = values => {
        const options = {onSuccess: closeForm};

        if (editingBoard) {
            updateBoard.mutate({id: editingBoard.id, values}, options);
            return;
        }

        createBoard.mutate(values, options);
    };

    const confirmDelete = boardId => {
        const board = boards.find(item => item.id === boardId);
        MySwal.fire({
            icon: "warning",
            title: "Excluir quadro?",
            text: `O quadro “${board?.name}” e todo o seu conteúdo serão excluídos. Esta ação não poderá ser desfeita.`,
            showCancelButton: true,
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: "#e64942",
            reverseButtons: true,
        }).then(({isConfirmed}) => {
            if (isConfirmed) deleteBoard.mutate(boardId);
        });
    };

    return (
        <TemplateDefault title="Organização de demandas" isLoading={usersLoading || boardsLoading}>
            <div className="h-[calc(100vh-74px)] min-h-[560px]">
                <BoardsLibrary
                    boards={boards}
                    onCreate={() => { setEditingBoard(null); setFormOpen(true); }}
                    onOpen={boardId => navigate(`/activity-control/demands/board/${boardId}`)}
                    onEdit={board => { setEditingBoard(board); setFormOpen(true); }}
                    onCopy={boardId => copyBoard.mutate(boardId)}
                    onDelete={confirmDelete}
                />
            </div>

            <BoardFormModal
                isOpen={formOpen}
                board={editingBoard}
                users={userOptions}
                onClose={closeForm}
                onSave={saveBoard}
                isSaving={createBoard.isPending || updateBoard.isPending}
            />
        </TemplateDefault>
    );
};

export default DemandOrganization;
